/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-use-before-define */
import {
  GetUserProjectsLimitedQuery,
  ProductName,
  UserStatus,
} from '@pypestream/api-services/urql';
import { sort, transformTimezonesData } from '@pypestream/utils';
import { inspect } from '@xstate/inspect';
import { initialize } from 'launchdarkly-js-client-sdk';
import {
  assign,
  interpret,
  Interpreter,
  Machine,
  Sender,
  State,
  StateMachine,
} from 'xstate';

import type {
  AvailableFeatureFlagKeys,
  FeatureFlagsType,
} from '@pypestream/feature-flags';
import { getGqlSdk } from '../gql-sdk';
import { CommonProject } from '../universal-nav/types';
import { handleGoHome } from '../utils/go-home';
import { hasErrors } from '../utils/has-errors';
import { WindowWithPypestreamGlobals } from './global';
import {
  Language,
  processUserProjects,
  SmartContext,
  SmartEvents,
  SmartState,
  SmartTypestates,
  Timezone,
  validateToolLevelAccess,
} from './smart.xstate-utils';

const win = window as WindowWithPypestreamGlobals;

const enableStateInspector = false;
if (enableStateInspector) {
  inspect({
    url: 'https://stately.ai/viz?inspect=1',
    iframe: false,
  });
}

const entryLogsAvalable = false;

const logOnEntryState = (stateName: string, info?: unknown) => {
  if (!entryLogsAvalable) return;
  console.log(
    `%csmart-state: ${stateName}`,
    'color: purple; font-size: larger; font-weight: bold',
    info || ''
  );
};

export type SmartMachineState = State<
  SmartContext,
  SmartEvents,
  any,
  SmartTypestates,
  any
>;

export const smartMachine: StateMachine<SmartContext, SmartState, SmartEvents> =
  Machine<SmartContext, SmartState, SmartEvents>(
    {
      predictableActionArguments: true,
      preserveActionOrder: true,
      id: 'smart-components',
      context: {
        config: undefined,
        app: ProductName.Organization,
        accountId: undefined,
        userInfo: undefined,
        userSettings: {},
        assignedProjects: undefined,
        allProducts: undefined,
        hasAvailableTools: undefined,
        languages: [],
        timezones: [],
        featureFlags: undefined,
        projectsLimitedUnsubscribe: undefined,
        allAssignedProjects: [],
        changeOrgSelection: undefined,
        validationError: undefined,
      },
      initial: 'unconfigured',
      states: {
        unconfigured: {
          on: {
            configure: {
              target: 'validating',
              actions: assign((_ctx, event) => ({
                config: event.config,
              })),
            },
          },
        },

        validating: {
          invoke: {
            id: 'validateBackend',
            src: async () => {
              const sdk = getGqlSdk();
              const { data, error } = await sdk.getLanguagesLimited();

              if (
                error?.graphQLErrors?.some(
                  (e: { message: string }) =>
                    e.message.includes('field') &&
                    e.message.includes('not found')
                )
              ) {
                throw new Error(
                  `Schema mismatch: ${error.graphQLErrors.map((e: { message: string }) => e.message).join(', ')}`
                );
              }

              if (error?.networkError) {
                throw new Error(
                  `Backend unreachable: ${(error.networkError as Error).message}`
                );
              }

              if (error) {
                throw new Error(`Backend validation failed: ${error.message}`);
              }

              return data;
            },
            onDone: {
              target: 'configured',
            },
            onError: {
              target: 'incompatible',
              actions: assign((_ctx, event) => ({
                validationError:
                  (event.data as Error)?.message || 'Unknown validation error',
              })),
            },
          },
        },

        incompatible: {},

        configured: {
          type: 'parallel',
          invoke: [
            {
              id: 'initializing-feature-flags',
              src: (ctx) => async (sendEvent: Sender<SmartEvents>) => {
                const ldKey =
                  ctx.config?.launchDarklyKey ||
                  import.meta.env.FE_LAUNCH_DARKLY_API_KEY;
                if (!ldKey) {
                  console.warn(
                    'Bypassed LD-initialization: no LaunchDarkly key provided.'
                  );
                  return Promise.resolve();
                }

                const client = initialize(
                  ldKey,
                  {
                    kind: 'user',
                    anonymous: true,
                  },
                  {
                    flushInterval: 900000,
                  }
                );

                client.on('change', (updatedFlags) => {
                  Object.keys(updatedFlags).forEach((updatedFlagKey) => {
                    sendEvent({
                      type: 'featureFlags.update',
                      updatedFlag: updatedFlagKey as AvailableFeatureFlagKeys,
                      updatedValue: updatedFlags[updatedFlagKey].current,
                    });
                  });
                });

                return client
                  .waitForInitialization()
                  .then(() => {
                    sendEvent({
                      type: 'featureFlags.initialized',
                      flags: Object.fromEntries(
                        Object.entries(client.allFlags())
                      ) as FeatureFlagsType,
                    });
                  })
                  .catch(() => {
                    console.warn(
                      "Bypassed LD-initialization as we didn't had FFs build time."
                    );
                    return Promise.resolve();
                  });
              },
            },
          ],
          on: {
            updateUserInfo: {
              actions: assign((_ctx, event) => {
                const { userInfo } = event;

                return {
                  userInfo: {
                    ..._ctx.userInfo,
                    ...userInfo,
                    status: userInfo.status || UserStatus.Invited,
                    email: _ctx.userInfo?.email || '',
                    defaultAccount: {
                      ..._ctx.userInfo?.defaultAccount,
                      id: userInfo.accountId,
                    },
                  },
                  ...(!_ctx.accountId && { accountId: userInfo.accountId }),
                };
              }),
            },
            changeApp: {
              actions: assign((_ctx, event) => ({
                app: event.app,
              })),
            },
            changeOrg: [
              {
                cond: (ctx, _event) => !ctx.userInfo,
                actions: assign((_ctx, event) => ({
                  accountId: event.orgId,
                  assignedProjects: undefined,
                  hasAvailableTools: true,
                })),
                target: '#smart-components.configured.userInfo.loading',
              },
              {
                cond: (ctx, _event) => !!ctx.userInfo,
                actions: assign((_ctx, event) => ({
                  accountId: event.orgId,
                  assignedProjects: undefined,
                  hasAvailableTools: true,
                })),
                target: '#smart-components.configured.projects.loading',
              },
            ],
            changeOrgSelection: {
              cond: (ctx, event) => !!ctx.accountId,
              actions: assign((ctx, event) => ({
                changeOrgSelection: event.orgId,
                assignedProjects: undefined,
                hasAvailableTools: true,
              })),
              target: '#smart-components.configured.projects.loading',
            },
            logout: {
              target: '#smart-components.configured.logout.loading',
            },
          },
          states: {
            featureFlags: {
              initial: 'idle',
              states: {
                idle: {
                  entry: (ctx, event) =>
                    logOnEntryState('featureFlags_idle', { ctx, event }),
                  on: {
                    'featureFlags.initialized': {
                      target:
                        '#smart-components.configured.featureFlags.loaded.withActualValue',
                      actions: [
                        assign((_ctx, data) => ({ featureFlags: data.flags })),
                      ],
                    },
                  },
                },
                loaded: {
                  entry: (ctx, event) =>
                    logOnEntryState('featureFlags_loaded', { ctx, event }),
                  on: {
                    'featureFlags.update': {
                      actions: assign((ctx, data) => {
                        let updatedContext = {
                          featureFlags: ctx.featureFlags,
                        };
                        if (ctx.featureFlags && data.updatedFlag) {
                          updatedContext = {
                            featureFlags: {
                              ...ctx.featureFlags,
                              [data.updatedFlag]: data.updatedValue,
                            },
                          };
                        }
                        return updatedContext;
                      }),
                    },
                  },
                  states: {
                    withActualValue: {
                      entry: (ctx, event) =>
                        logOnEntryState('featureFlags_withActualValue', {
                          ctx,
                          event,
                        }),
                    },
                    withDefaultValues: {
                      entry: (ctx, event) =>
                        logOnEntryState('featureFlags_withDefaultValues', {
                          ctx,
                          event,
                        }),
                    },
                  },
                },
              },
            },
            userInfo: {
              initial: 'loading',
              states: {
                idle: {
                  entry: (ctx, event) =>
                    logOnEntryState('userInfo_idle', { ctx, event }),
                  always: {
                    target: 'loading',
                  },
                },
                loading: {
                  entry: (ctx, event) =>
                    logOnEntryState('userInfo_loading', { ctx, event }),
                  invoke: {
                    id: 'getUserInfo',
                    src: async (_ctx) => {
                      const sdk = getGqlSdk();
                      const { data: user, error: userInfoError } =
                        await sdk.getUserInfoLimited();

                      if (hasErrors(user)) {
                        throw new Error(user.errors?.[0]?.message);
                      }

                      if (userInfoError) {
                        throw new Error(
                          `Failed to fetch user info: ${userInfoError.message}`
                        );
                      }

                      const userId = user?.admin_?.currentUser?.id;
                      const email = user?.admin_?.currentUser?.email;

                      if (!userId || !email) {
                        throw new Error(
                          `No user id found: ${userId} or email: ${email}`
                        );
                      }

                      const { data: userInfo, error: userSettingsError } =
                        await sdk.getUserSettingsLimited({ email });

                      if (hasErrors(userInfo)) {
                        throw new Error(userInfo.errors?.[0]?.message);
                      }

                      if (userSettingsError) {
                        throw new Error(
                          `Failed to fetch user settings: ${userSettingsError.message}`
                        );
                      }

                      return {
                        userInfo: user?.admin_?.currentUser,
                        userSettings: userInfo?.admin_,
                        defaultAccountId:
                          user?.admin_?.currentUser?.defaultAccount?.id,
                      };
                    },
                    onDone: {
                      target: ['loaded'],
                      actions: assign((ctx, event) => {
                        const { userInfo, userSettings, defaultAccountId } =
                          event.data;

                        return {
                          userInfo,
                          userSettings,
                          ...(!ctx.accountId && {
                            accountId: defaultAccountId,
                          }),
                        };
                      }),
                    },
                    onError: {
                      target: 'loadError',
                    },
                  },
                },
                loaded: {
                  entry: (ctx, event) => [
                    logOnEntryState('userInfo_loaded', { ctx, event }),
                    smartService.send('loadUserProjects'),
                  ],
                  on: {
                    updateUser: 'updating',
                    loadUser: 'loading',
                  },
                },
                loadError: {
                  entry: (ctx, event) =>
                    logOnEntryState('userInfo_loadError', { ctx, event }),
                },
                updating: {
                  entry: (ctx, event) =>
                    logOnEntryState('userInfo_updating', { ctx, event }),
                  id: 'updatingUser',
                  invoke: {
                    src: async (
                      ctx,
                      event
                    ): Promise<void | {
                      callback: ((res: boolean) => void) | undefined;
                      userInfo: SmartContext['userInfo'];
                    }> => {
                      if (event.type !== 'updateUser') {
                        return {
                          callback: undefined,
                          userInfo: ctx.userInfo,
                        };
                      }

                      const { userInfo } = event;
                      const sdk = getGqlSdk();
                      const { data, error: userUpdateError } =
                        await sdk.updateUserLimited(userInfo);

                      if (hasErrors(data)) {
                        console.error(data.errors?.[0]?.message);
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (userUpdateError) {
                        console.error(userUpdateError);
                        throw new Error(
                          `Failed to update user: ${userUpdateError.message}`
                        );
                      }

                      const updatedUserInfo: SmartContext['userInfo'] = {
                        ...ctx.userInfo,
                        ...userInfo,
                        picture: data?.admin_?.updateUser?.picture,
                        pictureFile: data?.admin_?.updateUser?.pictureFile,
                        status: userInfo.status || UserStatus.Active,
                        email: ctx.userInfo?.email || '',
                        defaultAccount: {
                          ...ctx.userInfo?.defaultAccount,
                          id: userInfo.accountId,
                        },
                      };

                      return {
                        callback: event.callback,
                        userInfo: updatedUserInfo,
                      };
                    },
                    onDone: {
                      target: 'loaded',
                      actions: assign((_ctx, event) => {
                        const { callback, userInfo } = event.data;
                        callback(true);
                        return {
                          userInfo,
                        };
                      }),
                    },
                    onError: {
                      target: 'loaded',
                    },
                  },
                },
              },
            },
            projects: {
              initial: 'idle',
              id: 'projects',
              on: {
                loadUserProjects: {
                  target: '.loading',
                  cond: (ctx) =>
                    ctx.userInfo?.id !== undefined &&
                    ctx.accountId !== undefined &&
                    !!ctx.app,
                },
                processProjectsSubscriptionUpdate: {
                  actions: assign((ctx, event) => {
                    if (event.type !== 'processProjectsSubscriptionUpdate') {
                      return ctx;
                    }

                    const allAssignedProjects = processUserProjects({
                      projects: event.data?.projects,
                      allProducts: ctx.allProducts || [],
                    });

                    ctx.allAssignedProjects = allAssignedProjects;
                    ctx.assignedProjects = allAssignedProjects.filter(
                      (proj) =>
                        proj.project.accountId ===
                        (ctx.changeOrgSelection || ctx.accountId)
                    );
                    return ctx;
                  }),
                },
              },
              states: {
                idle: {},
                loading: {
                  id: 'loadingProjects',
                  invoke: {
                    src: async (
                      ctx
                    ): Promise<{
                      products: GetUserProjectsLimitedQuery['tenancy_products'];
                      assignedProjects: CommonProject[];
                      allAssignedProjects: CommonProject[];
                    }> => {
                      const sdk = getGqlSdk();
                      const {
                        data: assignedUserProjects,
                        error: assignedUserProjectsError,
                      } = await sdk.getUserProjectsLimited({
                        userId: ctx.userInfo?.id as string,
                      });
                      if (assignedUserProjectsError) {
                        throw new Error(
                          `Failed to fetch user projects: ${assignedUserProjectsError.message}`
                        );
                      }

                      if (!assignedUserProjects) {
                        throw new Error('No user projects found');
                      }

                      const allAssignedProjects = processUserProjects({
                        projects: assignedUserProjects.tenancy_users_by_pk,
                        allProducts: assignedUserProjects.tenancy_products,
                      });

                      const assignedProjects = allAssignedProjects.filter(
                        (proj) =>
                          proj.project.accountId ===
                          (ctx.changeOrgSelection || ctx.accountId)
                      );

                      return {
                        products: assignedUserProjects.tenancy_products,
                        assignedProjects,
                        allAssignedProjects,
                      };
                    },
                    onDone: {
                      target: 'setupSubscription',
                      actions: [
                        assign((_ctx, event) => {
                          const { products } = event.data;
                          return {
                            allProducts: products,
                            assignedProjects: event.data.assignedProjects,
                            allAssignedProjects: event.data.allAssignedProjects,
                          };
                        }),
                      ],
                    },
                    onError: {
                      target: 'loadError',
                    },
                  },
                },
                setupSubscription: {
                  initial: 'validateInputs',
                  states: {
                    validateInputs: {
                      always: [
                        {
                          target: 'idle',
                          cond: (ctx) => !!ctx.accountId,
                        },
                      ],
                    },
                    idle: {
                      always: [
                        {
                          target: 'settingUp',
                          cond: (ctx) => validateToolLevelAccess(ctx, true),
                        },
                      ],
                    },
                    settingUp: {
                      invoke: {
                        src: async (ctx) => {
                          if (ctx.projectsLimitedUnsubscribe) {
                            ctx.projectsLimitedUnsubscribe();
                          }
                          const sdk = getGqlSdk();
                          const { subscribe } = sdk.projectsSubscriptionLimited(
                            {
                              userId: ctx.userInfo?.id as string,
                            }
                          );
                          const { unsubscribe } = subscribe((subData) => {
                            if (subData.error || !subData.data) {
                              console.error(
                                `Something went wrong with subscription setup: ${subData.error?.message}`
                              );
                            } else {
                              smartService.send(
                                'processProjectsSubscriptionUpdate',
                                {
                                  data: {
                                    projects: subData.data.tenancy_users_by_pk,
                                  },
                                }
                              );
                            }
                          });
                          return unsubscribe;
                        },
                        onDone: {
                          target: 'completed',
                          actions: assign((_ctx, event) => ({
                            projectsLimitedUnsubscribe: event.data,
                          })),
                        },
                        onError: {
                          target: 'error',
                        },
                      },
                    },
                    completed: {},
                    error: {},
                  },
                },
                navigateUserToHome: {
                  entry: (ctx) => {
                    handleGoHome({
                      accountId: ctx.accountId,
                      redirectedDueToNoToolAccess: true,
                      app: ctx.app,
                    });
                  },
                },
                loadError: {},
              },
            },
            languages: {
              id: 'languages',
              initial: 'loading',
              states: {
                loading: {
                  entry: (ctx, event) =>
                    logOnEntryState('languages_loading', { ctx, event }),
                  id: 'languagesLoading',
                  invoke: {
                    id: 'getLanguages',
                    src: async () => {
                      const sdk = getGqlSdk();
                      const { data: languages, error: languagesError } =
                        await sdk.getLanguagesLimited();

                      if (hasErrors(languages)) {
                        throw new Error(languages.errors?.[0]?.message);
                      }

                      if (languagesError) {
                        throw new Error(
                          `Failed to fetch languages: ${languagesError.message}`
                        );
                      }

                      return {
                        locales: languages?.admin_?.locales || [],
                        localizationSettingsConfig:
                          languages?.admin_?.localizationSettingsConfig,
                      };
                    },
                    onDone: {
                      target: 'loaded',
                      actions: assign((ctx, event) => {
                        const { locales, localizationSettingsConfig } =
                          event.data;
                        const languages: Language[] = sort<Language>(locales)
                          .asc('name')
                          .filter(
                            ({ languageCode }) =>
                              !languageCode ||
                              ['en', 'ar'].includes(languageCode)
                          )
                          .filter(
                            ({ locale }) =>
                              locale === 'ar-AE' ||
                              locale === 'en-US' ||
                              locale === ''
                          );

                        return {
                          languages,
                        };
                      }),
                    },
                    onError: {
                      target: 'loadError',
                    },
                  },
                },
                loaded: {
                  entry: (ctx, event) =>
                    logOnEntryState('languages_loaded', { ctx, event }),
                },
                loadError: {
                  entry: (ctx, event) =>
                    logOnEntryState('languages_loadError', { ctx, event }),
                },
              },
            },
            timezones: {
              id: 'timezones',
              initial: 'loading',
              states: {
                loading: {
                  entry: (ctx, event) =>
                    logOnEntryState('timezones_loading', { ctx, event }),
                  id: 'timezonesLoading',
                  invoke: {
                    id: 'getTimezones',
                    src: async () => {
                      const sdk = getGqlSdk();
                      const { data: timezones, error: timezonesError } =
                        await sdk.getTimeZonesLimited();

                      if (hasErrors(timezones)) {
                        throw new Error(timezones.errors?.[0]?.message);
                      }

                      if (timezonesError) {
                        throw new Error(
                          `Failed to fetch timezones: ${timezonesError.message}`
                        );
                      }

                      const timeZones = timezones?.admin_?.timeZones || [];

                      return transformTimezonesData<Timezone>(timeZones);
                    },
                    onDone: {
                      target: 'loaded',
                      actions: assign((ctx, event) => {
                        const timezones = sort<Timezone>(event.data).asc(
                          'label'
                        );

                        return {
                          timezones,
                        };
                      }),
                    },
                    onError: {
                      target: 'loadError',
                    },
                  },
                },
                loaded: {
                  entry: (ctx, event) =>
                    logOnEntryState('timezones_loaded', { ctx, event }),
                },
                loadError: {
                  entry: (ctx, event) =>
                    logOnEntryState('timezones_loadError', { ctx, event }),
                },
              },
            },
            logout: {
              id: 'logout',
              initial: 'idle',
              states: {
                idle: {
                  entry: (ctx, event) =>
                    logOnEntryState('logout_idle', { ctx, event }),
                },
                loading: {
                  entry: (ctx, event) =>
                    logOnEntryState('logout_loading', { ctx, event }),
                  invoke: {
                    src: async (_ctx, event) => {
                      if (event.type !== 'logout') {
                        return;
                      }

                      const sdk = getGqlSdk();
                      const { data, error } =
                        await sdk.falsifyUserActiveSessionsLimited();

                      if (hasErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(
                          `Failed to perform logout hook: ${error.message}`
                        );
                      }

                      event.callback();
                    },
                    onDone: {
                      target: 'loaded',
                    },
                    onError: {
                      target: 'error',
                    },
                  },
                },
                loaded: {
                  entry: (ctx, event) =>
                    logOnEntryState('logout_loaded', { ctx, event }),
                },
                error: {
                  entry: (ctx, event) =>
                    logOnEntryState('logout_error', { ctx, event }),
                },
              },
            },
          },
        },
      },
    },
    {
      actions: {},
    }
  );

export const smartService: Interpreter<
  SmartContext,
  SmartState,
  SmartEvents,
  SmartTypestates,
  any
> = interpret<SmartContext, SmartState, SmartEvents, SmartTypestates, any>(
  smartMachine,
  {
    devTools: enableStateInspector,
  }
);

win.pypestream = {
  smartService,
};
